import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
    checkResumeFileLocally,
    ResumeVerificationError,
    uploadAndVerifyResume,
} from "./resumeVerification";

/**
 * Always reject with a ResumeVerificationError so the UI can explain it.
 */
const verifyResume = async (file) => {
    try {
        return await uploadAndVerifyResume(file);
    } catch (error) {
        if (error instanceof ResumeVerificationError) {
            throw error;
        }
        console.error('Resume verification error:', error);
        throw new ResumeVerificationError('upload_failed');
    }
};

/**
 * State for picking a resume file and verifying it with the server.
 *
 * phase: 'select' | 'checking' | 'failed' | 'verified'
 *
 * @param {Object} options
 * @param {Function} [options.onVerified] Called with (user, file) once the resume passes
 */
const useResumeVerification = ({ onVerified } = {}) => {
    const [file, setFile] = useState(null);
    const [localError, setLocalError] = useState(null);

    const mutation = useMutation({
        mutationFn: verifyResume,
        retry: false,
        onSuccess: (user, verifiedFile) => onVerified?.(user, verifiedFile),
    });

    const error = localError || mutation.error;

    let phase = 'select';
    if (mutation.isLoading) phase = 'checking';
    else if (mutation.isSuccess) phase = 'verified';
    else if (error) phase = 'failed';

    const selectFile = (selected) => {
        mutation.reset();
        const problem = checkResumeFileLocally(selected);
        setLocalError(problem);
        setFile(problem ? null : selected);
    };

    const removeFile = () => {
        setFile(null);
        setLocalError(null);
        mutation.reset();
    };

    const verify = () => {
        if (file && !mutation.isLoading) {
            mutation.mutate(file);
        }
    };

    return {
        phase,
        file,
        error,
        verifiedUser: mutation.data,
        selectFile,
        removeFile,
        verify,
        reset: removeFile,
    };
};

export default useResumeVerification;
